// Suggests a more expressive word when the learner reaches for a very basic
// one, mirroring the "vocabulary upgrade" nudges a human tutor gives
// mid-conversation. Port of `tutor/Vocabulary.kt`.
import { mod } from './mod';

const upgrades: Readonly<Record<string, readonly string[]>> = {
  good: ['excellent', 'fantastic', 'solid', 'impressive'],
  bad: ['awful', 'terrible', 'disappointing', 'poor'],
  big: ['huge', 'enormous', 'massive', 'significant'],
  small: ['tiny', 'compact', 'modest', 'slight'],
  nice: ['lovely', 'pleasant', 'delightful', 'charming'],
  happy: ['delighted', 'thrilled', 'cheerful', 'content'],
  sad: ['down', 'disappointed', 'gloomy', 'miserable'],
  tired: ['exhausted', 'worn out', 'drained'],
  angry: ['furious', 'annoyed', 'irritated', 'frustrated'],
  very: ['extremely', 'incredibly', 'really quite'],
  'a lot': ['a great deal', 'plenty', 'loads'],
  said: ['mentioned', 'explained', 'pointed out', 'added'],
  thing: ['aspect', 'detail', 'item', 'matter'],
  get: ['receive', 'obtain', 'pick up'],
  like: ['enjoy', 'am fond of', 'am into'],
  hard: ['challenging', 'demanding', 'tough'],
  easy: ['straightforward', 'simple', 'effortless'],
  interesting: ['fascinating', 'intriguing', 'compelling'],
  beautiful: ['stunning', 'gorgeous', 'breathtaking'],
  fun: ['enjoyable', 'entertaining', 'a blast'],
  boring: ['dull', 'tedious', 'uninspiring'],
  important: ['crucial', 'essential', 'vital'],
  delicious: ['mouth-watering', 'tasty', 'flavourful'],
  scared: ['terrified', 'anxious', 'uneasy'],
  busy: ['swamped', 'hectic', 'tied up'],
  cheap: ['affordable', 'reasonable', 'budget-friendly'],
  expensive: ['pricey', 'costly', 'steep'],
  smart: ['clever', 'sharp', 'bright'],
  walk: ['stroll', 'wander', 'head over'],
  look: ['glance', 'gaze', 'check out'],
};

/** Natural phrases worth teaching when the matching keyword shows up. */
const idioms: Readonly<Record<string, string>> = {
  busy: '"I\'m swamped" is a very natural way to say you\'re extremely busy.',
  tired: 'Native speakers often say "I\'m wiped out" when they\'re really tired.',
  rain: 'When it rains hard, we say "it\'s pouring" or "it\'s pouring down".',
  expensive: '"It costs a fortune" is a lively way to say something is very expensive.',
  easy: 'If something is very easy, you can say "it was a piece of cake".',
  practice: '"I\'m getting the hang of it" means you\'re slowly mastering something.',
  weekend: '"I\'m going to take it easy this weekend" means you plan to relax.',
};

function paddedLower(text: string): string {
  return ` ${text.toLowerCase().replace(/[^a-z' ]/g, ' ')} `;
}

/** Returns a "try this instead" tip, or null when nothing stands out. */
export function upgradeTip(text: string, seed: number): string | null {
  const lower = paddedLower(text);
  const hits = Object.keys(upgrades).filter((k) => lower.includes(` ${k} `));
  if (hits.length === 0) return null;
  const word = hits[mod(seed, hits.length)]!;
  const options = upgrades[word]!;
  const suggestion = options[mod(seed, options.length)]!;
  return `Instead of "${word}", you could say "${suggestion}" — it sounds more natural and advanced.`;
}

/** Returns an idiom tip matching the message, or null. */
export function idiomTip(text: string, seed: number): string | null {
  const lower = paddedLower(text);
  const hits = Object.keys(idioms).filter((k) => lower.includes(` ${k} `));
  if (hits.length === 0) return null;
  return idioms[hits[mod(seed, hits.length)]!]!;
}
